import { jsxs, Fragment, jsx } from 'react/jsx-runtime';
import logger from '@agoras/logger';
import { getScreenSelector, ScreenWidthMinXXXLarge, mergeStyleSets, CommandBarButton, Callout } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { useContext, useState, useMemo } from 'react';
import { headerContext } from '../context.esm.js';
import AppsMenu from './AppsMenu/AppsMenu.esm.js';
import CategoriesMenu from './CategoriesMenu/CategoriesMenu.esm.js';
import ConsultingServicesMenu from './ConsultingServicesMenu/ConsultingServicesMenu.esm.js';
import IndustriesMenu from './IndustriesMenu/IndustriesMenu.esm.js';
import PartnersMenu from './PartnersMenu/PartnersMenu.esm.js';
import { useHydration } from '@agoras/hooks';
import { AppContext } from '../../context/context.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var Menus = function(param) {
    var unifiedExperience = param.unifiedExperience;
    return {
        Apps: {
            text: unifiedExperience ? 'Products' : 'Apps',
            component: AppsMenu
        },
        Categories: {
            text: 'Categories',
            component: CategoriesMenu
        },
        Industries: {
            text: 'Industries',
            component: IndustriesMenu
        },
        ConsultingServices: {
            text: unifiedExperience ? 'Professional Services' : 'Consulting Services',
            component: ConsultingServicesMenu
        },
        Partners: {
            text: 'Partners',
            component: PartnersMenu
        }
    };
};
var calloutStyles = {
    root: _define_property({
        width: "calc(100vw - 64px)"
    }, getScreenSelector(ScreenWidthMinXXXLarge, undefined), {
        width: ScreenWidthMinXXXLarge - 64,
        // Top and right are written inside inline style, !important is the only way to override it
        right: "0 !important"
    }),
    container: _define_property({}, getScreenSelector(ScreenWidthMinXXXLarge, undefined), {
        margin: '0 auto',
        width: ScreenWidthMinXXXLarge - 64
    })
};
var classNames = mergeStyleSets({
    menuButton: {
        height: '100%'
    }
});
var MegaMenu = function() {
    var unifiedExperience = useContext(AppContext).unifiedExperience;
    var _useState = _sliced_to_array(useState(), 2), selectedMenuKey = _useState[0], setSelectedMenuKey = _useState[1];
    var menuByStore = useMemo(function() {
        return Menus({
            unifiedExperience: unifiedExperience
        });
    }, [
        unifiedExperience
    ]);
    var selectedMenu = selectedMenuKey ? menuByStore[selectedMenuKey] : null;
    var _ref = selectedMenu || {}, SelectedMenuComponent = _ref.component;
    var t = useTranslation('header').t;
    var isHydrated = useHydration();
    return /*#__PURE__*/ jsxs(Fragment, {
        children: [
            Object.keys(menuByStore).filter(function(key) {
                // Hide Professional Services dropdown for unified experience
                if (key === 'ConsultingServices' && unifiedExperience) {
                    return false;
                }
                return true;
            }).map(function(key) {
                return /*#__PURE__*/ jsx(CommandBarButton, {
                    id: key,
                    className: classNames.menuButton,
                    checked: key === selectedMenuKey,
                    width: 32,
                    text: t(menuByStore[key].text),
                    disabled: !isHydrated,
                    onClick: function() {
                        logger.info(JSON.stringify({
                            key: key
                        }), _object_spread({
                            action: 'MegaMenu',
                            actionModifier: key === selectedMenuKey ? 'Collapse' : 'Open'
                        }, headerContext));
                        setSelectedMenuKey(key === selectedMenuKey ? undefined : key);
                    },
                    menuIconProps: {
                        iconName: 'ChevronDown'
                    }
                }, key);
            }),
            !!selectedMenu && /*#__PURE__*/ jsx(Callout, {
                target: "#".concat(selectedMenuKey),
                isBeakVisible: false,
                minPagePadding: 32,
                onDismiss: function() {
                    logger.info('', _object_spread({
                        action: 'MegaMenu',
                        actionModifier: 'Dismiss'
                    }, headerContext));
                    setSelectedMenuKey(undefined);
                },
                styles: calloutStyles,
                directionalHintFixed: true,
                children: !!SelectedMenuComponent && /*#__PURE__*/ jsx(SelectedMenuComponent, {
                    onLinkClick: function() {
                        return setSelectedMenuKey(undefined);
                    }
                })
            })
        ]
    });
};

export { MegaMenu as default };
